import React, { useEffect } from "react";
import PropTypes from "prop-types";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormInput from "Common/Custom/FormInput";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import FormTextarea from "Common/Custom/FormTextarea";
import FormSelect from "Common/Custom/FormSelect";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import { getSelectOptions } from "utils/optionHelper";
import useLaundryItems from "../../Hooks/useLaundryItems";
import LaundryItemTable from "../../Componates/LaundryItemTable/LaundryItemTable";

/**
 * AddLaundryForm — presentational component for creating a laundry record.
 *
 * Uses the shared useLaundryItems hook for all item row logic.
 * No infinite loop: items sync to parent via commitItems (called directly
 * in handleItemChange / addRow / deleteRow), not via a useEffect.
 */
const AddLaundryForm = ({
  values,
  errors,
  handleChange,
  handleSubmit,
  loading,
  fields,
  item,
  ProductOptions,
  EmployeesOptions,
  TaskOptions,
  setValues,
  validateItems,
  setErrors,
}) => {
  const optionConfig = {
    operated_by: {
      list: EmployeesOptions,
      valueKey: "employee_id",
      labelKey: "first_name",
    },
  };

  // Shared item row logic (add mode — no existingItems)
  const {
    items,
    grandTotal,
    originalQtyRef,
    handleItemChange,
    addRow,
    deleteRow,
    getAvailableProductOptions,
  } = useLaundryItems({
    TaskOptions,
    ProductOptions,
    existingItems: null, // add mode: no pre-fill
    setValues,
    setErrors,
    validateItems,
  });

  // When send_date changes, clear received_date if it's now before send_date
  useEffect(() => {
    if (values.send_date && values.received_date) {
      if (new Date(values.received_date) < new Date(values.send_date)) {
        handleChange({ target: { name: "received_date", value: "" } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.send_date]);

  // ── Render main form fields ───────────────────────────────────────────
  const renderFields = (fieldList) =>
    fieldList.map((field) => {
      switch (field.type) {
        case "select":
          return (
            <FormSelect
              key={field.name}
              field={field}
              value={values[field.name] ?? ""}
              handleChange={handleChange}
              options={getSelectOptions(field.name, optionConfig)}
              error={errors[field.name]}
            />
          );
        case "textarea":
          return (
            <FormTextarea
              key={field.name}
              field={field}
              value={values[field.name] ?? ""}
              handleChange={handleChange}
              error={errors[field.name]}
            />
          );
        case "date": {
          // send_date: min = today (no past dates)
          // received_date: min = send_date (cannot be before send date)
          const minDate =
            field.name === "received_date" && values.send_date
              ? values.send_date
              : new Date().toISOString().split("T")[0];
          return (
            <FormDatePicker
              key={field.name}
              field={{ ...field, minDate }}
              value={values[field.name] ?? ""}
              handleChange={handleChange}
              error={errors[field.name]}
              mode="date"
            />
          );
        }
        default:
          return (
            <FormInput
              key={field.name}
              field={field}
              value={values[field.name] ?? ""}
              handleChange={handleChange}
              error={errors[field.name]}
            />
          );
      }
    });

  return (
    <PageBody onSubmit={handleSubmit} headerchildren={<HeadingOne>Add Laundry</HeadingOne>}>
      {renderFields(fields)}

      <LaundryItemTable
        items={items}
        itemFields={item}
        errors={errors}
        TaskOptions={TaskOptions}
        ProductOptions={ProductOptions}
        originalQtyRef={originalQtyRef}
        handleItemChange={handleItemChange}
        addRow={addRow}
        deleteRow={deleteRow}
        getAvailableProductOptions={getAvailableProductOptions}
        grandTotal={grandTotal}
        isEditMode={false}
      />

      <FormSubmitButton label="Create" loading={loading} colSize="col-12" align="end" />
    </PageBody>
  );
};

AddLaundryForm.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  fields: PropTypes.array.isRequired,
  item: PropTypes.array.isRequired,
  ProductOptions: PropTypes.array.isRequired,
  EmployeesOptions: PropTypes.array.isRequired,
  TaskOptions: PropTypes.array.isRequired,
  setValues: PropTypes.func.isRequired,
  validateItems: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
};

AddLaundryForm.defaultProps = {
  errors: {},
  loading: false,
};

export default AddLaundryForm;
